import { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import {
  createModeratedPost,
  fetchPosts,
  fetchPostById,
  fetchModerationStats,
  deletePostByCustomId,
  updatePostByCustomId
} from "../redux/thunks/moderationThunks";

import {
  selectModerationPosts,
  selectModerationSelectedPost,
  selectModerationStats,
  selectModerationLoading,
  selectModerationCreateLoading,
  selectModerationErrorMessage,
  selectModerationPostsCount,
  selectHasPosts,
} from "../redux/selectors/moderationSelectors";

const PAGE_SIZE = 20;

const extractPostsFromPayload = (payload = {}) => {
  if (Array.isArray(payload)) return payload;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.posts && Array.isArray(payload.posts)) return payload.posts;
  if (payload && typeof payload === "object") {
    const keys = Object.keys(payload);
    for (const key of keys) {
      if (Array.isArray(payload[key])) return payload[key];
    }
  }
  return [];
};

export default function usePosts() {
  const dispatch = useDispatch();

  const posts = useSelector(selectModerationPosts);
  const selectedPost = useSelector(selectModerationSelectedPost);
  const stats = useSelector(selectModerationStats);
  const loading = useSelector(selectModerationLoading);
  const createLoading = useSelector(selectModerationCreateLoading);
  const error = useSelector(selectModerationErrorMessage);
  const postsCount = useSelector(selectModerationPostsCount);
  const hasPosts = useSelector(selectHasPosts);

  const [localError, setLocalError] = useState("");

  // Pagination state
  const [displayedPosts, setDisplayedPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const allPostsRef = useRef([]);

  /* ------------------------------------------------------------------ */

  const handleFetchPosts = useCallback(
    async ({} = {}) => {
      try {
        const res = await dispatch(fetchPosts()).unwrap();
        const all = extractPostsFromPayload(res);

        allPostsRef.current = all;

        const firstPage = all.slice(0, PAGE_SIZE);
        setDisplayedPosts(firstPage);
        setCurrentPage(1);
        setHasMore(all.length > PAGE_SIZE);

        return res;
      } catch (err) {
        const msg = err?.message || "Failed to fetch posts";
        setLocalError(msg);
        message.error(`Erreur lors du chargement: ${msg}`);
      }
    },
    [dispatch]
  );

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const nextSlice = allPostsRef.current.slice(0, nextPage * PAGE_SIZE);

    setDisplayedPosts(nextSlice);
    setCurrentPage(nextPage);
    setHasMore(nextSlice.length < allPostsRef.current.length);
    setLoadingMore(false);
  }, [currentPage, hasMore, loadingMore]);

  useEffect(() => {
    if (!loading && hasPosts && displayedPosts.length === 0) {
      const firstPage = posts.slice(0, PAGE_SIZE);
      allPostsRef.current = posts;
      setDisplayedPosts(firstPage);
      setCurrentPage(1);
      setHasMore(posts.length > PAGE_SIZE);
    }
  }, [loading, hasPosts, posts, displayedPosts.length]);

  // ✅ FIX: handleCreatePost retourne maintenant le résultat complet
  // pour que CreatePostBox puisse construire le post optimiste
  const handleCreatePost = useCallback(
    async (formData) => {
      setLocalError("");
      try {
        const res = await dispatch(createModeratedPost(formData)).unwrap();

        // ✅ Ne PAS afficher message.success ici car CreatePostBox gère l'UI
        // Retourner le résultat brut pour que le parent construise l'optimiste
        return res;
      } catch (err) {
        const msg = typeof err === 'string' ? err : err?.message || "Failed to create post";
        setLocalError(msg);
        return { success: false, message: msg };
      }
    },
    [dispatch]
  );

  const handleFetchPostById = useCallback(
    async (id) => {
      try {
        const res = await dispatch(fetchPostById(id)).unwrap();
        return res;
      } catch (err) {
        const msg = err?.message || "Failed to fetch post";
        setLocalError(msg);
      }
    },
    [dispatch]
  );

  const handleFetchStats = useCallback(async () => {
    try {
      const res = await dispatch(fetchModerationStats()).unwrap();
      return res;
    } catch (err) {
      const msg = err?.message || "Failed to fetch moderation stats";
      setLocalError(msg);
    }
  }, [dispatch]);

  const handleRefreshPosts = useCallback(async () => {
    return await handleFetchPosts();
  }, [handleFetchPosts]);

  const handleDeletePost = useCallback(
    async (idPost) => {
      try {
        await dispatch(deletePostByCustomId(idPost)).unwrap();
        message.success("🗑️ Post supprimé avec succès !");
        // ✅ Retire le post supprimé de la liste locale immédiatement
        setDisplayedPosts((prev) =>
          prev.filter(
            (p) => String(p.id_post ?? p.postId ?? p._id ?? p.id) !== String(idPost)
          )
        );
        allPostsRef.current = allPostsRef.current.filter(
          (p) =>
            String(p.id_post ?? p.postId ?? p._id ?? p.id) !== String(idPost)
        );
      } catch (err) {
        const msg = err?.message || "Échec de la suppression du post";
        message.error(msg);
        throw err;
      }
    },
    [dispatch]
  );

  const handleEditPost = useCallback(
    async (idPost, newText) => {
      try {
        const result = await dispatch(
          updatePostByCustomId({ idPost, data: { description: newText } })
        ).unwrap();
        if (result?.success === false) {
          throw new Error(result.message || 'Modification refusée par la modération');
        }
        const updatedPost = result?.post || result;
        if (updatedPost && typeof updatedPost === "object") {
          const matchesPost = (post) =>
            String(post.id_post ?? post.postId ?? post._id ?? post.id) === String(idPost);
          const mergedUpdate = { ...updatedPost, description: newText };

          setDisplayedPosts((prev) =>
            prev.map((post) => (matchesPost(post) ? { ...post, ...mergedUpdate } : post))
          );
          allPostsRef.current = allPostsRef.current.map((post) =>
            matchesPost(post) ? { ...post, ...mergedUpdate } : post
          );
        }
        message.success("✏️ Question updated successfully!");
      } catch (err) {
        const msg = err?.message || "Failed to update the question";
        message.error(msg);
        throw err;
      }
    },
    [dispatch]
  );

  return {
    posts,
    selectedPost,
    stats,
    loading,
    createLoading,
    error,
    postsCount,
    hasPosts,
    localError,

    // Pagination
    displayedPosts,
    hasMore,
    loadingMore,

    handleCreatePost,
    handleFetchPosts,
    handleFetchPostById,
    handleFetchStats,
    handleRefreshPosts,
    handleDeletePost,
    handleEditPost,
    handleLoadMore,
  };
}
